import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import Select from 'react-select';
import Decimal from 'decimal.js';
import { MAX_LIMIT } from 'modules/common/constants';
import { reactSelectCustomStyleCms } from 'helper';
import { get } from 'session';
import * as actions from '../actions';
import * as c from '../constant';

function ViewUpdateBusinessActivityItem({
  data,
  formData,
  setFormData,
  getNecessaryFieldsList,
  businessId,
  validateBusinessActivity,
  isLoading,
  validating,
}) {
  const applicationStatusId = get('applicationStatusId') || 0;
  const businessLineId = _.get(data, 'attributes.business_line_id');
  const dataActivityId = _.get(data, 'id', 0);
  const formActivities = _.get(formData, 'activities', []);
  const detailInfo = formActivities.find(
    (item) => item.activity_id === dataActivityId
  );
  const yearlyDetails = _.get(detailInfo, 'yearly_details', {});
  const yearlyGrossTotal = _.get(detailInfo, 'yearly_gross_amount', {});
  const [fieldList, setFieldList] = useState([]);
  const GROSS_AMOUNT_TYPES = [
    'gross_amount_essential',
    'gross_amount_nonessential',
  ];
  const filteredNecessaryFields = fieldList.filter(
    (item) => !item.quantity_matrix
  );
  // const filteredQuantityMatrix = fieldList.find((item) => item.quantity_matrix);
  const filteredQuantityMatrix = fieldList.filter(
    item => _.isArray(item.quantity_matrix) && item.quantity_matrix.length > 0
  );
  // const quantityMatrix = filteredQuantityMatrix?.quantity_matrix || [];

  useEffect(() => {
    if (businessLineId && applicationStatusId) {
      const args = {
        business_line_id: businessLineId,
        application_status_id: +applicationStatusId,
      };
      getNecessaryFieldsList(args, ({ data: fields }) => {
        setFieldList(fields);
      });
    }

    return () => setFieldList([]);
  }, [businessLineId, applicationStatusId]);

  const calculateYearTotal = (yearData) => {
    if (!yearData) return 0;

    return Object.entries(yearData)
      .filter(([key]) => GROSS_AMOUNT_TYPES.some((type) => key.toLowerCase().includes(type)))
      .reduce((sum, [, value]) => {
        const total = new Decimal(sum);
        const amount = new Decimal(value || '0');
        return total.plus(amount);
      }, new Decimal('0'))
      .toString();
  };

  const hasGrossAmountMultiplier = fieldList.some((field) => {
    const multiplier = _.get(field, 'multiplier', '').toLowerCase();
    return GROSS_AMOUNT_TYPES.some((type) => multiplier.includes(type));
  });

  const onChangeDetails = (year, type, value) => {
    setFormData({
      ...formData,
      activities: formData.activities.map((activity) => {
        if (+activity.activity_id === +dataActivityId) {
          const updatedYearlyDetails = {
            ...activity.yearly_details,
            [year]: {
              ...activity.yearly_details[year],
              [type]: value,
            },
          };

          // Calculate yearly_gross_amount for each year
          const updatedYearlyGross = {};
          Object.keys(updatedYearlyDetails).forEach((yearKey) => {
            updatedYearlyGross[yearKey] = calculateYearTotal(
              updatedYearlyDetails[yearKey]
            );
          });

          return {
            ...activity,
            yearly_details: updatedYearlyDetails,
            ...(hasGrossAmountMultiplier && {
              yearly_gross_amount: updatedYearlyGross,
            }),
          };
        }
        return activity;
      }),
    });
  };

  const onChangeDetailsQuantityMatrix = (year, multiplier) => (e) => {
    setFormData({
      ...formData,
      activities: formData.activities.map((activity) => {
        if (+activity.activity_id === +dataActivityId) {
          const updatedYearlyDetailsQuantityMatrix = {
            ...activity.yearly_details,
            [year]: {
              ...activity.yearly_details[year],
              quantity_matrix: {
                ...activity.yearly_details[year].quantity_matrix,
                [multiplier]: e.target.value,
              },
            },
          };

          // Calculate yearly_gross_amount for each year
          const updatedYearlyGross = {};
          Object.keys(updatedYearlyDetailsQuantityMatrix).forEach((yearKey) => {
            updatedYearlyGross[yearKey] = calculateYearTotal(
              updatedYearlyDetailsQuantityMatrix[yearKey]
            );
          });

          return {
            ...activity,
            yearly_details: updatedYearlyDetailsQuantityMatrix,
            ...(hasGrossAmountMultiplier && {
              yearly_gross_amount: updatedYearlyGross,
            }),
          };
        }
        return activity;
      }),
    });
  };

  const handleGrossAmountChange = (value, year) => {
    setFormData((prev) => {
      const activities = formActivities.map((activity) => {
        const activityId = _.get(activity, 'activity_id');

        if (+activityId === +dataActivityId) {
          return {
            ...activity,
            yearly_gross_amount: {
              ...activity.yearly_gross_amount,
              [year]: value,
            },
          };
        }
        return activity;
      });

      return {
        ...prev,
        activities,
      };
    });
  };

  const handleValidateBusinessActivity = (datas) => (e) => {
    e.preventDefault();
    const businessActivityId = _.get(datas, 'id');

    const args = {
      business_line_id: businessLineId,
      application_status_id: +applicationStatusId,
    };

    const grossAmount = {
      gross_amount: _.get(datas, 'attributes.gross_amount') || 0,
    };

    validateBusinessActivity(
      businessId,
      businessActivityId,
      grossAmount,
      () => {
        getNecessaryFieldsList(args, ({ data: fields }) => {
          setFieldList(fields);
        });
      }
    );
  };

  return (
    <div className="d-flex flex-column">
      {isLoading ? (
        <div className="loading">
          <div className="spinner text-center">
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        </div>
      ) : (
        <>
          <label className="form-label mt-3">Year</label>
          <div className="form-row px-1">
            {Object.keys(data.attributes.yearly_gross_amount).map((year) => {
              return (
                <div className="row mb-3" key={year}>
                  <div className="form-group col-12">
                    <label htmlFor={year} className="form-label">
                      Yearly Gross Amount {year}
                    </label>
                    <NumberFormat
                      name={year}
                      id={year}
                      placeholder="0.00"
                      className="form-control"
                      onValueChange={(values) => {
                        const { value } = values;
                        handleGrossAmountChange(value, year);
                      }}
                      displayType="input"
                      decimalScale={2}
                      isNumericString
                      allowNegative
                      thousandSeparator
                      allowLeadingZeros
                      prefix="₱ "
                      value={yearlyGrossTotal[year]}
                      disabled={hasGrossAmountMultiplier}
                      isAllowed={(values) => {
                        const { value } = values;
                        if (!value) return true;
                        const val = new Decimal(value);
                        const max = new Decimal(MAX_LIMIT);
                        return value === undefined || val.lte(max);
                      }}
                    />
                  </div>

                  <div className="form-group col-12">
                    <label className="form-label">Yearly Details</label>
                  </div>

                  {filteredNecessaryFields.map((item) => {
                    const multiplier = _.get(item, 'multiplier');
                    const options = _.get(item, 'options');
                    const itemValue = _.get(yearlyDetails[year], multiplier);
                    const hasGrossKey = multiplier.includes('gross_amount_');

                    if (options) {
                      const selectOptions = options.map((option) => ({
                        label: _.get(option, 'multiplier', '')
                          .replace(/_/g, ' ')
                          .replace(/^(.)|\s+(.)/g, (x) => x.toUpperCase()),
                        value: _.get(option, 'multiplier', ''),
                      }));
                      return (
                        <div className="col-sm-12 col-md-6" key={multiplier}>
                          <div className="form-group">
                            <label
                              htmlFor={multiplier}
                              className="form-label"
                              style={{ opacity: '.7' }}
                            >
                              {(multiplier || '').replace(/_/g, ' ')}{' '}
                            </label>
                            <Select
                              styles={reactSelectCustomStyleCms()}
                              options={selectOptions}
                              value={selectOptions.find((option) => {
                                const optionValue = _.get(option, 'value');
                                return itemValue === optionValue;
                              })}
                              onChange={({ value }) =>
                                onChangeDetails(year, multiplier, value)
                              }
                              components={{ IndicatorSeparator: () => null }}
                              placeholder="Select"
                            />
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="col-sm-12 col-md-6" key={multiplier}>
                        <div className="form-group">
                          <label
                            htmlFor={_.get(item, 'label')}
                            className="form-label"
                            style={{ opacity: '.7' }}
                          >
                            {(multiplier || '').replace(/_/g, ' ')}{' '}
                          </label>
                          <NumberFormat
                            name={multiplier}
                            id={multiplier}
                            placeholder="0.00"
                            className="form-control"
                            onValueChange={(values) => {
                              const { value } = values;
                              onChangeDetails(year, multiplier, value);
                            }}
                            displayType="input"
                            decimalScale={2}
                            isNumericString
                            allowNegative
                            thousandSeparator
                            allowLeadingZeros
                            prefix={hasGrossKey ? '₱ ' : ''}
                            value={itemValue}
                            isAllowed={(values) => {
                              const { value } = values;
                              if (!hasGrossKey || !value) return true;
                              const val = new Decimal(value);
                              const max = new Decimal(MAX_LIMIT);
                              return value === undefined || val.lte(max);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {!_.isEmpty(filteredQuantityMatrix) ? (
                    filteredQuantityMatrix.map((item) => {
                      const multiplier = _.get(item, 'multiplier', '');

                      return (
                        <React.Fragment>
                          <div className='col-12 form-group mb-0'>
                            <label className='form-label text-capitalize' style={{ opacity: .7 }}>
                              {multiplier || ''}
                            </label>
                          </div>
                          <div className="w-100 border rounded-lg mb-2 mx-3">
                            <table className="table table-borderless table-sm table-striped table-hover mb-0">
                              <thead>
                                <tr className="form-group">
                                  <td className="col-4 text-center form-label border-right">Unit</td>
                                  <td className="col-4 text-center form-label border-right">Capacity</td>
                                  <td className="col-4 text-center form-label">Number of Units</td>
                                </tr>
                              </thead>
                              <tbody>
                                {item.quantity_matrix.map((quantityMatrixItem) => {
                                  const quantityMatrixMultiplier = _.get(quantityMatrixItem, 'multiplier');
                                  const quantityMatrixItemValue = _.get(
                                    yearlyDetails[year].quantity_matrix,
                                    quantityMatrixMultiplier,
                                    ''
                                  );

                                  return (
                                    <tr
                                      key={quantityMatrixMultiplier}
                                      className="border-top form-group"
                                    >
                                      <td className="col-4 text-left form-label border-right">
                                        {_.get(quantityMatrixItem, 'unit')}
                                      </td>
                                      <td className="col-4 text-left form-label border-right">
                                        {_.get(quantityMatrixItem, 'capacity')}
                                      </td>
                                      <td className="col-4 text-left form-label">
                                        <input
                                          className="form-control"
                                          type="text"
                                          name={quantityMatrixMultiplier}
                                          onChange={onChangeDetailsQuantityMatrix(
                                            year,
                                            quantityMatrixMultiplier
                                          )}
                                          value={quantityMatrixItemValue || ''}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </React.Fragment>
                      )
                    })
                  ) : (
                    ''
                  )}
                </div>
              );
            }) || ''}
          </div>
          <div className="text-right">
            <button
              type="button"
              className="btn btn-primary cms-bg-color-primary cms-outline-primary font-weight-bold"
              onClick={handleValidateBusinessActivity(data)}
            >
              {validating ? (
                <i className="fa fa-spin fa-spinner" />
              ) : (
                `Validate`
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

ViewUpdateBusinessActivityItem.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  formData: PropTypes.instanceOf(Object).isRequired,
  setFormData: PropTypes.instanceOf(Function).isRequired,
  getNecessaryFieldsList: PropTypes.instanceOf(Function).isRequired,
  businessId: PropTypes.string.isRequired,
  validateBusinessActivity: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  validating: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ api }) => {
  return {
    isLoading: api.loading.indexOf(c.GET_NECESSARY_FIELD_LIST) > -1,
    validating: api.loading.indexOf(c.VALIDATE_BUSINESS_ACTIVITY) > -1,
  };
};

const enhance = _.flowRight([
  withRouter,
  connect(mapStateToProps, {
    ...actions,
  }),
]);

export default enhance(ViewUpdateBusinessActivityItem);
